// ── Date formatting ───────────────────────────────────────
export function formatDate(val) {
  if (!val) return "—";
  let ms = typeof val === "number" ? val : parseInt(val);
  if (isNaN(ms)) return val;
  // Detect Excel serial number (Lark stores some dates this way)
  // Excel serial: days since 1900-01-01, typically < 100000
  if (ms < 100000) {
    // Convert Excel serial to ms timestamp
    ms = (ms - 25569) * 86400 * 1000;
  }
  return new Date(ms).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    timeZone: "Asia/Kolkata",
  });
}

export function formatDateTime(val) {
  if (!val) return "—";
  let ms = typeof val === "number" ? val : parseInt(val);
  if (isNaN(ms)) return val;
  if (ms < 100000) {
    ms = (ms - 25569) * 86400 * 1000;
  }
  return new Date(ms).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
    timeZone: "Asia/Kolkata",
  });
}

// ── Global Config ───────────────────────────────────────────
export const STATUS_OPTIONS = [
  "⬜ Not Started", 
  "🟡 In Progress", 
  "✅ Done", 
  "🔴 Overdue", 
  "🔴 Overdue (Done)"
];

// ── Status badge config ───────────────────────────────────
export function getStatusBadge(status) {
  if (!status) return { cls: "badge-gray", label: "—" };
  const s = String(status).toLowerCase();
  if (s.includes("overdue")) return { cls: "badge-danger", label: status };
  if (s.includes("done") || s.includes("complet")) return { cls: "badge-success", label: status };
  if (s.includes("progress")) return { cls: "badge-warning", label: status };
  if (s.includes("not started") || s.includes("⬜")) return { cls: "badge-gray", label: status };
  return { cls: "badge-gray", label: status };
}

export function getPriorityBadge(priority) {
  if (!priority) return { cls: "badge-gray", label: "—", color: "#6B7280", bg: "#F3F4F6" };
  const p = String(priority).toLowerCase();
  
  if (p === "high" || p === "important") {
    return { cls: "badge-danger", label: "⚡ High", color: "#991B1B", bg: "#FEE2E2" };
  }
  if (p === "medium" || p === "normal") {
    return { cls: "badge-warning", label: "Medium", color: "#92400E", bg: "#FEF3C7" };
  }
  if (p === "low") {
    return { cls: "badge-info", label: "Low", color: "#1E40AF", bg: "#DBEAFE" };
  }
  
  // Fallback for unknown data
  return { cls: "badge-gray", label: priority, color: "#64748B", bg: "#F1F5F9" };
}

// ── Extract display name from user field ──────────────────
export function getUserName(val) {
  if (!val) return "—";
  if (typeof val === "string") return val;
  if (Array.isArray(val)) {
    return val.map((v) => {
      if (typeof v === "string") return v;
      return v?.en_name || v?.name || v?.email || "?";
    }).join(", ");
  }
  if (typeof val === "object") {
    if (val.users) return val.users.map((u) => u.en_name || u.name || u.email).join(", ");
    return val.en_name || val.name || val.email || "?";
  }
  return String(val);
}

export function getUserEmail(val) {
  if (!val) return null;
  if (typeof val === "object" && !Array.isArray(val)) {
    if (val.email) return val.email;
    if (val.users?.[0]?.email) return val.users[0].email;
  }
  if (Array.isArray(val)) {
    return val[0]?.email || null;
  }
  return null;
}

// ── Check if a user in a field value (by userId or name) ──
export function userMatchesField(fieldVal, userOrName) {
  if (!fieldVal || !userOrName) return false;

  // userOrName can be a string (name) or object (JWT user with userId, name, email)
  const userId = typeof userOrName === "object" ? userOrName.userId : null;
  const name   = typeof userOrName === "object" 
    ? (userOrName.name || "").toLowerCase().trim()
    : (userOrName || "").toLowerCase().trim();

  const check = (v) => {
    if (!v) return false;
    // Match by Lark open_id first (most reliable)
    if (userId && typeof v === "object") {
      if (v.open_id === userId || v.id === userId) return true;
    }
    if (typeof v === "string") return name ? v.toLowerCase().includes(name) : false;
    if (typeof v === "object") {
      return (
        (v.en_name || "").toLowerCase().includes(name) ||
        (v.name || "").toLowerCase().includes(name) ||
        (v.email || "").toLowerCase().includes(name)
      );
    }
    return false;
  };

  if (Array.isArray(fieldVal)) {
    if (fieldVal.some(check)) return true;
    // Handle {users:[...]} wrapper
    for (const item of fieldVal) {
      if (item?.users && item.users.some(check)) return true;
    }
    return false;
  }
  if (fieldVal?.users) return fieldVal.users.some(check);
  return check(fieldVal);
}

// ── Cell value renderer (plain text) ─────────────────────
export function cellText(val) {
  if (val === null || val === undefined) return "";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  if (typeof val === "number") {
    if (val > 946684800000 && val < 4102444800000) return formatDate(val);
    return String(val);
  }
  if (typeof val === "string") return val;
  if (Array.isArray(val)) {
    return val.map((v) => {
      if (typeof v === "string") return v;
      if (typeof v === "boolean") return v ? "Yes" : "No";
      return v?.en_name || v?.name || v?.text || v?.email || "";
    }).filter(Boolean).join(", ");
  }
  if (typeof val === "object") {
    if (val.users) return val.users.map((u) => u.en_name || u.name).join(", ");
    const str = val.name || val.en_name || val.text || val.label || val.value;
    if (str) return String(str);
    return "";
  }
  return String(val);
}

// ── Get all unique employees from records ─────────────────
export function extractEmployees(adminRecords) {
  const map = {};
  for (const rec of adminRecords) {
    const users = rec.fields?.["Assigned Users"] || rec.fields?.["Assigned By"];
    const process = (v) => {
      if (!v) return;
      const name = v.en_name || v.name;
      const email = v.email;
      if (name && !map[name]) map[name] = { name, email };
    };
    if (Array.isArray(users)) users.forEach(process);
    else if (users?.users) users.users.forEach(process);
    else process(users);
  }
  return Object.values(map);
}

// ── Performance Calculation ──────────────────────────────
export const normalizeToMs = (val) => {
  if (!val) return 0;
  let ms = 0;
  if (typeof val === "number") ms = val;
  else if (typeof val === "object" && val?.value) ms = val.value;
  else if (Array.isArray(val) && val[0]?.value) ms = val[0].value;
  else {
    const d = new Date(val);
    ms = isNaN(d.getTime()) ? 0 : d.getTime();
  }
  if (ms <= 0) return 0;
  // Handle Excel serial numbers (Lark specific)
  if (ms < 1000000) { 
     ms = (ms - 25569) * 86400 * 1000;
  }
  return ms;
};

export function calculatePerformanceRating(tasks) {
  if (!tasks || tasks.length === 0) return 0;

  const now = Date.now();
  let total = tasks.length;
  let pendingCount = 0;
  let overdueCount = 0;
  let withinTimeCount = 0;
  let afterTimeCount = 0;
  let lateDaysTotal = 0;
  let overdueDaysTotal = 0;

  tasks.forEach(task => {
    const f = task.fields || {};
    const isDone = !!f["My Completion"];
    const dueTime = normalizeToMs(f["End Date"]);
    const completionTime = normalizeToMs(f["Completed At"] || f["Completion Time"]);

    if (!isDone) {
      if (dueTime > now || dueTime === 0) {
        pendingCount++;
      } else {
        overdueCount++;
        overdueDaysTotal += (now - dueTime) / (1000 * 60 * 60 * 24);
      }
    } else {
      // 1 minute grace period for on-time completion
      if (completionTime <= (dueTime + 60000) || dueTime === 0) {
        withinTimeCount++;
      } else {
        afterTimeCount++;
        lateDaysTotal += (completionTime - dueTime) / (1000 * 60 * 60 * 24);
      }
    }
  });

  const denom = total - pendingCount + overdueCount;
  if (denom <= 0) return 0;

  const scoreWithin = (withinTimeCount / denom) * 10;
  const scoreAfter = (afterTimeCount / denom) * 10;
  const penaltyLate = afterTimeCount > 0 ? (0.1 * (lateDaysTotal / afterTimeCount)) : 0;
  const penaltyOverdue = overdueCount > 0 ? (0.1 * (overdueDaysTotal / overdueCount)) : 0;

  let finalScore = (scoreWithin + scoreAfter - penaltyLate) - penaltyOverdue;
  
  return Math.max(0, Math.min(10, finalScore)); 
}
